/**
 * Generation du journal de paie mensuel
 *
 * Transforme les bulletins de paie en ecritures comptables
 * selon le plan comptable configure.
 *
 * Niveaux de detail :
 * - global : une seule ecriture par poste (total societe)
 * - salarie : une ecriture par salarie par poste
 * - centre_cout : une ecriture par centre de cout par poste
 */

import type { ChartOfAccounts } from './chart-of-accounts'
import { DEFAULT_CHART } from './chart-of-accounts'
import type { PayrollResult } from '../paie/payroll-engine'

export type DetailLevel = 'global' | 'salarie' | 'centre_cout'

export interface JournalEntry {
  date: string
  journal: string
  accountCode: string
  accountLabel: string
  debitCents: number
  creditCents: number
  label: string
  reference: string
}

export interface PayrollJournalInput {
  periodYear: number
  periodMonth: number
  payrollResults: Array<{
    employeeId: string
    employeeName: string
    department: string
    result: PayrollResult
  }>
  chart?: ChartOfAccounts
  detailLevel?: DetailLevel
}

/**
 * Genere le journal de paie mensuel.
 */
export function generatePayrollJournal(input: PayrollJournalInput): JournalEntry[] {
  const chart = input.chart ?? DEFAULT_CHART
  const level = input.detailLevel ?? 'global'
  const date = `${input.periodYear}-${String(input.periodMonth).padStart(2, '0')}-31`
  const ref = `PAIE ${String(input.periodMonth).padStart(2, '0')}/${input.periodYear}`
  const entries: JournalEntry[] = []

  if (level === 'global') {
    // Agreger tous les salaries
    const totals = aggregateResults(input.payrollResults.map(p => p.result))
    entries.push(...generateEntriesFromTotals(totals, chart, date, ref, ''))
  } else {
    // Une serie d'ecritures par salarie (ou par centre de cout)
    const groups = level === 'centre_cout'
      ? groupBy(input.payrollResults, p => p.department || 'SANS_DEPARTEMENT')
      : groupBy(input.payrollResults, p => p.employeeId)

    for (const [key, members] of Object.entries(groups)) {
      const totals = aggregateResults(members.map(m => m.result))
      const groupLabel = level === 'salarie'
        ? members[0]?.employeeName ?? key
        : key
      entries.push(...generateEntriesFromTotals(totals, chart, date, ref, groupLabel))
    }
  }

  return entries
}

interface AggregatedTotals {
  grossBaseCents: number
  overtimeCents: number
  thirteenthMonthCents: number
  benefitsInKindCents: number
  ssEmployeeMaladieCents: number
  ssEmployeePensionCents: number
  ssEmployeeDependanceCents: number
  rtsCents: number
  ssEmployerMaladieCents: number
  ssEmployerPensionCents: number
  ssEmployerAccidentCents: number
  ssEmployerMutualiteCents: number
  ssEmployerSanteCents: number
  aVerserCents: number
  mealVouchersEmployerCents: number
}

function aggregateResults(results: PayrollResult[]): AggregatedTotals {
  return {
    grossBaseCents: results.reduce((s, r) => s + r.grossBaseCents, 0),
    overtimeCents: results.reduce((s, r) => s + r.overtimeCents, 0),
    thirteenthMonthCents: results.reduce((s, r) => s + r.thirteenthMonthCents, 0),
    benefitsInKindCents: results.reduce((s, r) => s + r.benefitsInKindCents, 0),
    ssEmployeeMaladieCents: results.reduce((s, r) => s + r.ssEmployeeMaladieCents, 0),
    ssEmployeePensionCents: results.reduce((s, r) => s + r.ssEmployeePensionCents, 0),
    ssEmployeeDependanceCents: results.reduce((s, r) => s + r.ssEmployeeDependanceCents, 0),
    rtsCents: results.reduce((s, r) => s + r.rtsCents, 0),
    ssEmployerMaladieCents: 0, // TODO: ajouter au PayrollResult les details patronaux
    ssEmployerPensionCents: 0,
    ssEmployerAccidentCents: 0,
    ssEmployerMutualiteCents: 0,
    ssEmployerSanteCents: 0,
    aVerserCents: results.reduce((s, r) => s + r.aVerserCents, 0),
    mealVouchersEmployerCents: results.reduce((s, r) => s + r.mealVouchersEmployerCents, 0),
  }
}

function generateEntriesFromTotals(
  t: AggregatedTotals,
  chart: ChartOfAccounts,
  date: string,
  ref: string,
  label: string,
): JournalEntry[] {
  const entries: JournalEntry[] = []
  const journal = chart.defaultJournal

  const add = (code: string, debit: number, credit: number) => {
    const mapping = chart.mappings.find(m => m.payrollItemCode === code)
    if (!mapping || (debit === 0 && credit === 0)) return
    entries.push({
      date,
      journal,
      accountCode: debit > 0 ? mapping.debitAccount : mapping.creditAccount,
      accountLabel: mapping.label,
      debitCents: debit,
      creditCents: credit,
      label: label ? `${mapping.label} — ${label}` : mapping.label,
      reference: ref,
    })
  }

  // Charges de remuneration (debit)
  add('SALAIRE_BASE', t.grossBaseCents, 0)
  if (t.overtimeCents > 0) add('HEURES_SUP', t.overtimeCents, 0)
  if (t.thirteenthMonthCents > 0) add('TREIZIEME_MOIS', t.thirteenthMonthCents, 0)
  if (t.benefitsInKindCents > 0) add('AVANTAGE_NATURE', t.benefitsInKindCents, 0)

  // Retenues salariales (credit organismes)
  add('CSS_MALADIE_SAL', 0, t.ssEmployeeMaladieCents)
  add('CSS_PENSION_SAL', 0, t.ssEmployeePensionCents)
  add('CSS_DEPENDANCE', 0, t.ssEmployeeDependanceCents)
  add('RTS', 0, t.rtsCents)

  // Charges patronales (debit charges, credit organismes)
  add('CSS_MALADIE_PAT', t.ssEmployerMaladieCents, 0)
  add('CSS_PENSION_PAT', t.ssEmployerPensionCents, 0)
  add('CSS_ACCIDENT', t.ssEmployerAccidentCents, 0)
  add('CSS_MUTUALITE', t.ssEmployerMutualiteCents, 0)
  add('CSS_SANTE', t.ssEmployerSanteCents, 0)

  // Net a payer (credit banque)
  add('NET_A_PAYER', 0, t.aVerserCents)

  // Cheques-repas
  if (t.mealVouchersEmployerCents > 0) {
    add('CHEQUES_REPAS', t.mealVouchersEmployerCents, 0)
  }

  return entries
}

function groupBy<T>(items: T[], keyFn: (item: T) => string): Record<string, T[]> {
  const groups: Record<string, T[]> = {}
  for (const item of items) {
    const key = keyFn(item)
    if (!groups[key]) groups[key] = []
    groups[key].push(item)
  }
  return groups
}

// ─── Export CSV ──────────────────────────────────────────────────

export type CSVFormat = 'sage_bob' | 'exact_online' | 'generic'

/**
 * Exporte le journal en CSV.
 */
export function exportJournalCSV(
  entries: JournalEntry[],
  format: CSVFormat,
): string {
  switch (format) {
    case 'sage_bob':
      return exportSageBob(entries)
    case 'exact_online':
      return exportExactOnline(entries)
    case 'generic':
    default:
      return exportGeneric(entries)
  }
}

/** Sage BOB : ;-separated, ANSI encoding, date DD/MM/YYYY */
function exportSageBob(entries: JournalEntry[]): string {
  const header = 'Journal;Date;Compte;Libelle;Debit;Credit;Reference'
  const lines = entries.map(e => [
    e.journal,
    formatDateDDMMYYYY(e.date),
    e.accountCode,
    e.label.replace(/;/g, ','),
    e.debitCents > 0 ? centsToDecimal(e.debitCents) : '',
    e.creditCents > 0 ? centsToDecimal(e.creditCents) : '',
    e.reference,
  ].join(';'))
  return [header, ...lines].join('\r\n') + '\r\n'
}

/** Exact Online : comma-separated, date YYYY-MM-DD */
function exportExactOnline(entries: JournalEntry[]): string {
  const header = 'Journal,Date,GLAccount,Description,AmountDC,D/C,Reference'
  const lines = entries.flatMap(e => {
    const rows: string[] = []
    if (e.debitCents > 0) {
      rows.push([e.journal, e.date, e.accountCode, `"${e.label}"`, centsToDecimal(e.debitCents), 'D', e.reference].join(','))
    }
    if (e.creditCents > 0) {
      rows.push([e.journal, e.date, e.accountCode, `"${e.label}"`, centsToDecimal(e.creditCents), 'C', e.reference].join(','))
    }
    return rows
  })
  return [header, ...lines].join('\r\n') + '\r\n'
}

/** Format generique configurable */
function exportGeneric(entries: JournalEntry[]): string {
  const header = 'Date;Journal;Compte;Libelle;Debit;Credit;Reference'
  const lines = entries.map(e => [
    e.date,
    e.journal,
    e.accountCode,
    e.label.replace(/;/g, ','),
    e.debitCents > 0 ? centsToDecimal(e.debitCents) : '0.00',
    e.creditCents > 0 ? centsToDecimal(e.creditCents) : '0.00',
    e.reference,
  ].join(';'))
  return [header, ...lines].join('\r\n') + '\r\n'
}

function centsToDecimal(cents: number): string {
  return (cents / 100).toFixed(2)
}

function formatDateDDMMYYYY(isoDate: string): string {
  const [y, m, d] = isoDate.split('-')
  return `${d}/${m}/${y}`
}
